import React, { useEffect, useState } from 'react'
import { useStyles } from './ButtonStyling.styles'
import { SelectChangeEvent, Typography } from '@mui/material'
import {
  ColorSelectorV2,
  SelectV2,
  SwitchV2,
  SwitchVarient,
  TextInputV2,
} from '@labrav/react-components'
import { useFormikContext } from 'formik'
import clsx from 'clsx'
import {
  extractColorV2,
  hexadecimalColor,
} from '../../../../common/utils/productCreationUtil'
import { convertRGBAAlpha } from '../../../../common/utils/convertRGBAAlpha'
import { calculateMinMaxResult } from '../../../AddEditProductFormComponents/FlyOutProductBranding/FlyOutProductBranding'
import { useStyles as commonStyles } from '../../FlyOutStorefront.styles'
import {
  borderStrokeWidthSizes,
  fontNames,
  fontSizes,
  fontWeights,
} from '../../../../utils/templateCreationConstants'
import { isBoolean } from 'lodash'
import {
  DataType,
  FormField,
} from '@labrav/react-components/lib/@types/types/forms'
import { hexToRgb, rgbToHex } from '../../../../utils/colorsConvertor'
import { ButtonInCreation } from '../../../../modules/storefront/buttonCreation/reducer'

export const ButtonStyling: React.FC = () => {
  const { classes } = useStyles()
  const { classes: commonClasses } = commonStyles()
  const { values, errors, touched, setFieldValue, handleBlur, handleChange } =
    useFormikContext<ButtonInCreation>()
  const { buttonData } = values

  useEffect(() => {
    if (values?.buttonData?.buttons) {
      const updatedButtons = values.buttonData.buttons.map(button => ({
        ...button,
        customization: {
          ...(button.customization || {}),
          ...buttonData.buttonCustomization,
        },
      }))

      setFieldValue('buttonData.buttons', updatedButtons)
    }
  }, [buttonData.buttonCustomization])

  const [textColorType, setTextColorType] = useState<string>(
    buttonData.buttonCustomization?.color
      ? buttonData.buttonCustomization?.color?.includes('#')
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )

  const [textColor, setTextColor] = useState<string[]>(() => {
    const colorString = buttonData.buttonCustomization?.color || ''
    return extractColorV2(textColorType, colorString)
  })

  useEffect(() => {
    const colorString = buttonData.buttonCustomization?.color || ''
    setTextColor(extractColorV2(textColorType, colorString))
  }, [buttonData.buttonCustomization?.color])

  const [backgroundColorType, setBackgroundColorType] = useState<string>(
    buttonData.buttonCustomization?.backgroundColor
      ? buttonData.buttonCustomization?.backgroundColor?.includes('#')
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )

  const [backgroundColor, setBackgroundColor] = useState<string[]>(() => {
    const colorString = buttonData.buttonCustomization?.backgroundColor || ''
    return extractColorV2(backgroundColorType, colorString)
  })

  useEffect(() => {
    const colorString = buttonData.buttonCustomization?.backgroundColor || ''
    setBackgroundColor(extractColorV2(backgroundColorType, colorString))
  }, [buttonData.buttonCustomization?.backgroundColor])

  const [borderColorType, setBorderColorType] = useState<string>(
    buttonData.buttonCustomization?.borderColor
      ? buttonData.buttonCustomization?.borderColor?.includes('#')
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )

  const [borderColor, setBorderColor] = useState<string[]>(() => {
    const colorString = buttonData.buttonCustomization?.borderColor || ''
    return extractColorV2(borderColorType, colorString)
  })

  useEffect(() => {
    const colorString = buttonData.buttonCustomization?.borderColor || ''
    setBorderColor(extractColorV2(borderColorType, colorString))
  }, [buttonData.buttonCustomization?.borderColor])

  const handleTypeChange = async (event: SelectChangeEvent, type: string) => {
    const typeValue = event.target.value as string
    switch (type) {
      case 'TEXT_COLOR': {
        const resetValue =
          typeValue === 'RGB'
            ? hexToRgb(textColor[4]).toString()
            : rgbToHex(
                parseInt(textColor[0]),
                parseInt(textColor[1]),
                parseInt(textColor[2])
              )
        await setTextColorType(event.target.value as string)
        await setFieldValue('buttonData.buttonCustomization.color', resetValue)
        break
      }
      case 'BACKGROUND_COLOR': {
        const resetValue =
          typeValue === 'RGB'
            ? hexToRgb(backgroundColor[4]).toString()
            : rgbToHex(
                parseInt(backgroundColor[0]),
                parseInt(backgroundColor[1]),
                parseInt(backgroundColor[2])
              )
        await setBackgroundColorType(event.target.value as string)
        await setFieldValue(
          'buttonData.buttonCustomization.backgroundColor',
          resetValue
        )
        break
      }
      case 'BORDER_COLOR': {
        const resetValue =
          typeValue === 'RGB'
            ? hexToRgb(borderColor[4]).toString()
            : rgbToHex(
                parseInt(borderColor[0]),
                parseInt(borderColor[1]),
                parseInt(borderColor[2])
              )
        await setBorderColorType(event.target.value as string)
        await setFieldValue(
          'buttonData.buttonCustomization.borderColor',
          resetValue
        )
        break
      }
      default:
        break
    }
  }

  const handleColorChange = async (
    index: number,
    event: SelectChangeEvent,
    type: string
  ) => {
    switch (type) {
      case 'TEXT_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...textColor],
          event.target.value as string
        )

        if (textColorType === 'RGB') {
          const color = `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
          await setFieldValue('buttonData.buttonCustomization.color', color)
        } else {
          const color = hexadecimalColor(
            `${updatedColorValues[4]}`,
            Number(updatedColorValues[3])
          )
          await setFieldValue('buttonData.buttonCustomization.color', color)
        }
        return
      }
      case 'BACKGROUND_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...backgroundColor],
          event.target.value as string
        )

        if (backgroundColorType === 'RGB') {
          const color = `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
          await setFieldValue(
            'buttonData.buttonCustomization.backgroundColor',
            color
          )
        } else {
          const color = hexadecimalColor(
            `${updatedColorValues[4]}`,
            Number(updatedColorValues[3])
          )
          await setFieldValue(
            'buttonData.buttonCustomization.backgroundColor',
            color
          )
        }
        return
      }
      case 'BORDER_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...borderColor],
          event.target.value as string
        )

        if (borderColorType === 'RGB') {
          const color = `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
          await setFieldValue(
            'buttonData.buttonCustomization.borderColor',
            color
          )
        } else {
          const color = hexadecimalColor(
            `${updatedColorValues[4]}`,
            Number(updatedColorValues[3])
          )
          await setFieldValue(
            'buttonData.buttonCustomization.borderColor',
            color
          )
        }
        return
      }
      default:
        break
    }
  }

  // Card Shadow
  const isButtonShadow = isBoolean(
    buttonData?.buttonCustomization?.buttonShadow
  )
    ? buttonData?.buttonCustomization?.buttonShadow
    : false
  const handleChangeButtonShadow = (e: {
    target: {
      checked: boolean
    }
  }) => {
    setFieldValue(
      'buttonData.buttonCustomization.buttonShadow',
      e?.target?.checked
    )
  }
  const [inputFont, setInputFont] = useState(
    buttonData?.card?.customization?.font
  )
  useEffect(() => {
    if (!fontNames.includes(inputFont as string)) {
      setInputFont('other')
    }
  }, [])

  const handleFontChange = (e: SelectChangeEvent) => {
    const selectedFont = e.target.value as string
    if (selectedFont === 'other') {
      setInputFont('other')
      setFieldValue('buttonData.card.customization.font', '')
    } else {
      setInputFont(selectedFont)
      setFieldValue('buttonData.card.customization.font', selectedFont)
    }
  }

  const fontNamesFields: FormField = {
    title: 'Select font family',
    placeHolder: 'Select font family',
    id: 'buttonData.card.customization.font',
    isRequired: false,
    humanReadableKey: 'selectFontSize',
    isCustom: false,
    defaultValue: inputFont,
    dataType: 'enum' as DataType,
    options: fontNames,
    isReadOnly: false,
  }

  return (
    <div
      className={clsx(
        classes.sectionWrapper,
        classes.sectionHeaderWrapper,
        classes.name
      )}
      data-testid="button-styling-component"
    >
      <div className={classes.textContentWrapper}>
        <Typography
          className={classes.textHeading}
          data-testid="button-styling-text"
        >
          Button styling
        </Typography>
      </div>
      <div
        className={classes.sectionInnerWrapper}
        data-testid="button-styling-inner-component"
      >
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="button-styling-font"
          >
            Text font
          </Typography>
          <div
            className={classes.textInput}
            data-testid="card-style-font-input"
          >
            <SelectV2
              field={{ ...fontNamesFields }}
              value={inputFont}
              onChange={handleFontChange}
              onBlur={handleBlur}
            />
          </div>
        </div>
        {inputFont === 'other' && (
          <div className={classes.rowContainer}>
            <div className={classes.sectionTitle}></div>
            <div
              className={classes.textInput}
              data-testid="card-style-font-input"
            >
              {
                <TextInputV2
                  keyName="buttonData.card.customization.font"
                  title="Font family"
                  type="text"
                  placeHolder=""
                  value={buttonData?.card?.customization?.font || ''}
                  isError={
                    !!(
                      (touched as any).buttonData?.card?.customization?.font &&
                      (errors as any).buttonData?.card?.customization?.font
                    )
                  }
                  onChange={handleChange}
                  updatedValue={buttonData?.card?.customization?.font || ''}
                  isReadOnly={false}
                  isRequired={false}
                  errorText={
                    !!(touched as any).buttonData?.card?.customization?.font &&
                    ((errors as any).buttonData?.card?.customization?.font ||
                      '')
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              }
            </div>
          </div>
        )}
        {/* fontweight and size */}
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="button-styling-broke-stroke-width"
          >
            Font properties
          </Typography>
          <div className={classes.fieldWrapper}>
            <div>
              <SelectV2
                data-testid="card-header-text-font-size-selector"
                minWidth="119px"
                field={{
                  title: 'Font Size',
                  placeHolder: 'Font Size',
                  id: 'buttonData.buttonCustomization.fontSize',
                  isRequired: false,
                  humanReadableKey: 'fontSize',
                  isReadOnly: false,
                  isCustom: false,
                  defaultValue: '12px',
                  dataType: 'string',
                  options: fontSizes,
                }}
                value={buttonData.buttonCustomization?.fontSize || '12px'}
                key={'buttonData.buttonCustomization.fontSize'}
                onChange={handleChange}
                onBlur={handleBlur}
                isError={false}
                errorText={''}
                isRequired={false}
              />
            </div>
            <div>
              <SelectV2
                data-testid="card-header-text-font-weight-selector"
                minWidth="119px"
                field={{
                  title: 'Font Weight',
                  placeHolder: 'Font Weight',
                  id: 'buttonData.buttonCustomization.fontWeight',
                  isRequired: false,
                  humanReadableKey: 'fontWeight',
                  isReadOnly: false,
                  isCustom: false,
                  defaultValue: '400',
                  dataType: 'string',
                  options: fontWeights,
                }}
                value={buttonData.buttonCustomization?.fontWeight || '400'}
                key={'buttonData.buttonCustomization.fontWeight'}
                onChange={handleChange}
                onBlur={handleBlur}
                isError={false}
                errorText={''}
                isRequired={false}
              />
            </div>
          </div>
        </div>
        {/* colors */}
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="button-styling-text-color"
          >
            Text color
          </Typography>
          <div className={classes.colorSelectorWidth}>
            <ColorSelectorV2
              data-testid="button-style-text-color-selector"
              selectedField={textColorType}
              placeholder={'#FFFFFF'}
              onBlur={handleBlur}
              onChange={event => handleTypeChange(event, 'TEXT_COLOR')}
              keyName={'buttonData.buttonCustomization.color'}
              colorValues={textColor}
              handleColorChange={(index, event) =>
                handleColorChange(index, event, 'TEXT_COLOR')
              }
              color={convertRGBAAlpha(
                buttonData?.buttonCustomization?.color || '',
                false
              )}
              isError={
                !!(
                  (touched as any).buttonData?.buttonCustomization?.color &&
                  (errors as any).buttonData?.buttonCustomization?.color
                )
              }
              errorText={
                !!(touched as any).buttonData?.buttonCustomization?.color &&
                ((errors as any).buttonData?.buttonCustomization?.color || '')
              }
              secondaryVariant={true}
            />
          </div>
        </div>
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="background-color"
          >
            Background color
          </Typography>
          <div className={classes.colorSelectorWidth}>
            <ColorSelectorV2
              data-testid="background-selector"
              selectedField={backgroundColorType}
              placeholder={'#FFFFFF'}
              onBlur={handleBlur}
              onChange={event => handleTypeChange(event, 'BACKGROUND_COLOR')}
              keyName={'buttonData.buttonCustomization.backgroundColor'}
              colorValues={backgroundColor}
              handleColorChange={(index, event) =>
                handleColorChange(index, event, 'BACKGROUND_COLOR')
              }
              color={convertRGBAAlpha(
                buttonData?.buttonCustomization?.backgroundColor || '',
                false
              )}
              isError={
                !!(
                  (touched as any).buttonData?.buttonCustomization
                    ?.backgroundColor &&
                  (errors as any).buttonData?.buttonCustomization
                    ?.backgroundColor
                )
              }
              errorText={
                !!(touched as any).buttonData?.buttonCustomization
                  ?.backgroundColor &&
                ((errors as any).buttonData?.buttonCustomization
                  ?.backgroundColor ||
                  '')
              }
              secondaryVariant={true}
            />
          </div>
        </div>
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="border-color"
          >
            Border color
          </Typography>
          <div className={classes.colorSelectorWidth}>
            <ColorSelectorV2
              data-testid="border-selector"
              selectedField={borderColorType}
              placeholder={'#FFFFFF'}
              onBlur={handleBlur}
              onChange={event => handleTypeChange(event, 'BORDER_COLOR')}
              keyName={'buttonData.buttonCustomization.borderColor'}
              colorValues={borderColor}
              handleColorChange={(index, event) =>
                handleColorChange(index, event, 'BORDER_COLOR')
              }
              color={convertRGBAAlpha(
                buttonData?.buttonCustomization?.borderColor || '',
                false
              )}
              isError={
                !!(
                  (touched as any).buttonData?.buttonCustomization
                    ?.borderColor &&
                  (errors as any).buttonData?.buttonCustomization?.borderColor
                )
              }
              errorText={
                !!(touched as any).buttonData?.buttonCustomization
                  ?.borderColor &&
                ((errors as any).buttonData?.buttonCustomization?.borderColor ||
                  '')
              }
              secondaryVariant={true}
            />
          </div>
        </div>
        {/* border stroke width */}
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="button-styling-border-stroke-width"
          >
            Border stroke width
          </Typography>
          <div>
            <SelectV2
              data-testid="border-stroke-width-selector"
              minWidth="119px"
              field={{
                id: 'buttonData.buttonCustomization.borderWidth',
                isRequired: false,
                humanReadableKey: 'borderStrokeWidth',
                isReadOnly: false,
                isCustom: false,
                defaultValue: '1px',
                dataType: 'string',
                options: borderStrokeWidthSizes,
              }}
              value={buttonData?.buttonCustomization?.borderWidth || '1px'}
              key={'buttonData.buttonCustomization.borderWidth'}
              onChange={handleChange}
              onBlur={handleBlur}
              isError={false}
              errorText={''}
              isRequired={false}
            />
          </div>
        </div>
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="button-styling-rounded-corners"
          >
            Rounded corner radius
          </Typography>
          <div>
            <SelectV2
              data-testid="rounded-corners-selector"
              minWidth="119px"
              field={{
                id: 'buttonData.buttonCustomization.borderRadius',
                isRequired: false,
                humanReadableKey: 'borderRadius',
                isReadOnly: false,
                isCustom: false,
                defaultValue: '4px',
                dataType: 'string',
                options: fontSizes,
              }}
              value={buttonData?.buttonCustomization?.borderRadius || '4px'}
              key={'buttonData.buttonCustomization.borderRadius'}
              onChange={handleChange}
              onBlur={handleBlur}
              isError={false}
              errorText={''}
              isRequired={false}
            />
          </div>
        </div>
        <div className={classes.rowContainer}>
          <Typography
            className={classes.sectionTitle}
            data-testid="button-styling-card-shadow"
          >
            Button shadow
          </Typography>
          <SwitchV2
            data-testid="button-shadow-switch"
            checked={isButtonShadow}
            switchVarient={SwitchVarient.DARK}
            onChange={handleChangeButtonShadow}
          />
        </div>
      </div>
    </div>
  )
}
